import { useEffect, useRef } from "react";

export const PERSIST_DEBOUNCE_MS = 500;

/**
 * Persist a growing battle event log to localStorage without
 * re-serializing the whole array on every appended event.
 *
 * - Appends are debounced (trailing ~500 ms): a burst of events from
 *   a single action collapses into one storage write.
 * - An empty log removes the key immediately and cancels any pending
 *   write (the "fresh match" reset must not race a stale write).
 * - `terminal` removes the key immediately and cancels any pending
 *   write — a finished match's gameId is never reused, so the cached
 *   log is garbage.
 * - A pending write is flushed (not dropped) on unmount, so a refresh
 *   right after an event still rehydrates the full feed.
 */
export function useDebouncedLogPersist(
  key: string | null,
  log: readonly unknown[],
  terminal: boolean,
  debounceMs: number = PERSIST_DEBOUNCE_MS,
): void {
  const timerRef = useRef<number | null>(null);
  // Latest props for the unmount flush below.
  const latestRef = useRef({ key, log, terminal });
  latestRef.current = { key, log, terminal };

  useEffect(() => {
    if (!key) return;
    // Cancel in the effect body (not a cleanup) so an unmount leaves
    // the pending timer alive for the flush effect's cleanup to see.
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (terminal || log.length === 0) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Private-mode / quota failure — log loss is acceptable.
      }
      return;
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      try {
        window.localStorage.setItem(key, JSON.stringify(log));
      } catch {
        // Quota / private-mode failure — log loss is acceptable.
      }
    }, debounceMs);
  }, [key, log, terminal, debounceMs]);

  // Flush (don't drop) any pending write when the page unmounts.
  useEffect(() => {
    return () => {
      if (timerRef.current === null) return;
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
      const latest = latestRef.current;
      if (!latest.key || latest.terminal || latest.log.length === 0) return;
      try {
        window.localStorage.setItem(latest.key, JSON.stringify(latest.log));
      } catch {
        // Quota / private-mode failure — log loss is acceptable.
      }
    };
  }, []);
}
